<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-dateSettings-styleAndAnimation"
  >
    <opacity-settings v-model="opacity" />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <label class="sidebar-form-label">Display format</label>
          <select
            v-model.lazy="format"
            data-test="graphics-selectDateFormat"
          >
            <option
              v-for="format in formats"
              :key="format.format"
              :value="format.format"
              :data-test="`graphics-selectDateFormat-option-${format.format}`"
            >
              {{ format.display }}
            </option>
          </select>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <label
            class="checkbox"
          >
            Custom format
            <input
              v-model="customFormat"
              type="checkbox"
              data-test="graphics-dateSettings-toggleCustomFormat"
              data-entity="graphics-dateSettings-toggleCustomFormat"
            />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
        </div>
      </div>

      <div v-if="customFormat" class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <input
            v-model="customFormatStyle"
            type="text"
            :class="{ 'is-invalid': formatError }"
            name="format"
            data-test="graphics-dateSettings-customFormat"
            @change="formatDate"
          />
          <label class="sidebar-input-sub-label">Custom date format</label>
          <span v-if="formatError" class="text-tiny danger">{{
            formatError
          }}</span
          ><br />
          <span class="text-tiny"
            ><a href="https://date-fns.org/v2.15.0/docs/format" target="_blank"
              >Formatting documention</a
            ></span
          >
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <label class="checkbox">
            Custom time zone
            <input
              v-model="customTimeZone"
              type="checkbox"
              data-test="graphics-dateSettings-toggleCustomTimeZone"
            />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
        </div>
      </div>

      <div v-if="customTimeZone" class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <select
            v-model="customTimeZoneData"
            data-test="graphics-dateSettings-selectTimeZone"
          >
            <option
              v-for="timezone in timezones"
              :key="timezone.name"
              :value="timezone"
              :data-test="`graphics-dateSettings-selectTimeZone-option-${timezone.name}`"
            >
              {{ timezone.name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Font</label>

      <font-dropdown
        :custom-fonts="customFontsArray"
        :element="element"
      />

      <div class="settings-row settings-row-xs">
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            v-model.number="fontSize"
            rules="required|numeric|min:0"
            min="0"
            :class="{ 'is-invalid': validationErrors['fontSize'] }"
            type="number"
            name="fontSize"
            data-test="graphics-dateSettings-fontSize"
            data-entity="graphics-dateSettings-fontSize"
            @input.stop.prevent="textChange"
          />
          <label class="sidebar-input-sub-label">Size</label>
        </div>
        <div class="settings-col-6 settings-col-xs">
          <validation-field as="input"
            v-model.number="letterSpacing"
            rules="required|numeric|min:0"
            type="number"
            name="letterSpacing"
            min="0"
            :class="{ 'is-invalid': validationErrors['letterSpacing'] }"
            data-test="graphics-dateSettings-letterSpacing"
            data-entity="graphics-dateSettings-letterSpacing"
            @input.stop.prevent="textChange"
          />
          <label class="sidebar-input-sub-label">Letter spacing</label>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="colorpicker">
            <colorpicker v-model="color" />
          </div>
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Text styles</label>

      <div class="settings-row settings-row-xs text-styles">
        <div class="settings-col-12 settings-col-xs simple">
          <button
            class="btn btn-sm btn-icon"
            :class="getFontWeightBtnClass"
            data-test="graphics-dateSettings-bold"
            data-entity="graphics-dateSettings-bold"
            @click="setFontWeight"
          >
            <span class="fa-solid fa-bold"></span>
          </button>
          <button
            class="btn btn-sm btn-icon"
            :class="getFontStyleBtnClass"
            data-test="graphics-dateSettings-italic"
            data-entity="graphics-dateSettings-italic"
            @click="setFontStyle"
          >
            <span class="fa-solid fa-italic"></span>
          </button>
        </div>
      </div>

      <label class="sidebar-form-label">Text options</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group text-decorations">
            <button
              v-for="style in textDecorations"
              :key="style.value"
              type="button"
              class="btn btn-sm"
              :class="[
                { selected: textDecoration === style.value },
              ]"
              data-test="graphics-dateSettings-textDecoration"
              data-entity="graphics-dateSettings-textDecoration"
              @click="setDecorationStyle(style.value)"
            >
              <span class="icon" :class="'icon-' + style.icon"></span>
            </button>
          </div>
          <label class="sidebar-input-sub-label">Decoration</label>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group text-transforms">
            <button
              v-for="style in textTransforms"
              :key="style.value"
              type="button"
              class="btn btn-sm"
              :class="[
                { selected: textTransform === style.value },
              ]"
              :data-test="`graphics-dateSettings-textTransform-${style.value}`"
              :data-entity="`graphics-dateSettings-textTransform-${style.value}`"
              @click="setTransformStyle(style.value)"
            >
              <span class="icon" :class="'icon-' + style.icon"></span>
            </button>
          </div>
          <label class="sidebar-input-sub-label">Transform</label>
        </div>
      </div>

      <label class="sidebar-form-label">Text alignment</label>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group">
            <button
              v-for="align in alignments"
              :key="align"
              type="button"
              :class="[{ selected: textAlign === align }]"
              class="btn btn-sm"
              :data-test="`graphics-dateSettings-align-${align}`"
              :data-entity="`graphics-dateSettings-align-${align}`"
            @click="setAlignment(align)"
          >
              <span class="fa-solid" :class="'fa-align-' + align"></span>
            </button>
          </div>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <div class="btn-group vertical-alignments">
            <button
              v-for="align in verticalAlignments"
              :key="align"
              type="button"
              :class="[{ selected: verticalAlign === align }]"
              class="btn btn-sm"
              :data-test="`graphics-dateSettings-verticalAlign-${align}`"
              :data-entity="`graphics-dateSettings-verticalAlign-${align}`"
              @click="setVerticalAlignment(align)"
            >
              <span class="fak" :class="`fa-text-align-${align}`"></span>
            </button>
          </div>
        </div>
      </div>

      <div class="settings-row settings-row-xs">
        <div class="settings-col-12 settings-col-xs">
          <label class="checkbox"
            >Shrink text to fit
            <input
              v-model="fitText"
              type="checkbox"
              data-test="graphics-dateSettings-toggleFitText"
              data-entity="graphics-dateSettings-toggleFitText"
              @change="resizeText"
            />
            <div class="checkmark">
              <span class="checkmark-tick fa-solid fa-check"></span>
            </div>
          </label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox"
        >Text shadow
        <input
          v-model="textShadow"
          type="checkbox"
          data-test="graphics-dateSettings-toggleTextShadow"
          data-entity="graphics-dateSettings-toggleTextShadow"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div v-if="textShadow" class="settings-row settings-row-xs">
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            v-model.number="textShadowX"
            rules="required|integer"
            type="number"
            name="textShadowX"
            :class="{ 'is-invalid': validationErrors['textShadowX'] }"
            data-test="graphics-dateSettings-textShadowX"
            data-entity="graphics-dateSettings-textShadowX"
          />
          <label class="sidebar-input-sub-label">X</label>
        </div>
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            v-model.number="textShadowY"
            rules="required|integer"
            type="number"
            name="textShadowY"
            :class="{ 'is-invalid': validationErrors['textShadowY'] }"
            data-test="graphics-dateSettings-textShadowY"
            data-entity="graphics-dateSettings-textShadowY"
          />
          <label class="sidebar-input-sub-label">Y</label>
        </div>
        <div class="settings-col-3 settings-col-xs">
          <validation-field as="input"
            v-model.number="textShadowBlur"
            rules="required|integer"
            type="number"
            name="textShadowBlur"
            :class="{ 'is-invalid': validationErrors['textShadowBlur'] }"
            data-test="graphics-dateSettings-textShadowBlur"
            data-entity="graphics-dateSettings-textShadowBlur" 
          />
          <label class="sidebar-input-sub-label">Blur</label>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker">
          <colorpicker v-model="textShadowColor" />
          <label class="sidebar-input-sub-label">Color</label>
        </div>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import { format as dateFormat } from 'date-fns';
import { mapGetters } from 'vuex';
import timezones from './timezones';
import EventBus from '../../../utils/event-bus';
import SettingsMixins from '../../../mixins/settings-mixins';
import AnimationSettings from '../Animation.vue';
import OpacitySettings from '../Opacity.vue';
import BoxShadowSettings from '../BoxShadow.vue';
import FontDropdown from '../FontDropdown.vue';

export default {
  mixins: [SettingsMixins],
  props: ['element'],
  components: {
    'animation-settings': AnimationSettings,
    'opacity-settings': OpacitySettings,
    'box-shadow-settings': BoxShadowSettings,
    FontDropdown,
  },
  data() {
    return {
      timezones,
      formatError: null,
      textDecorations: [
        { value: 'none', icon: 'text-normal' },
        { value: 'underline', icon: 'text-underline' },
        { value: 'line-through', icon: 'text-strikethrough' },
      ],
      textTransforms: [
        { value: 'none', icon: 'text-normal' },
        { value: 'uppercase', icon: 'text-uppercase' },
        { value: 'lowercase', icon: 'text-lowercase' },
      ],
      alignments: ['left', 'center', 'right', 'justify'],
      verticalAlignments: ['top', 'middle', 'bottom'],
    };
  },
  computed: {
    ...mapGetters(['customFontsArray', 'customFonts']),
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    format: {
      get() {
        return this.getPropertyValue('format');
      },
      set(v) {
        this.updateOneElementValue('format', v);
      },
    },
    customFormat: {
      get() {
        return this.getPropertyValue('customFormat');
      },
      set(v) {
        this.updateOneElementValue('customFormat', v);
      },
    },
    customFormatStyle: {
      get() {
        return this.getPropertyValue('customFormatStyle');
      },
      set(v) {
        this.updateOneElementValue('customFormatStyle', v);
      },
    },
    // zips/unzips data so we can just use one v-model to change
    customTimeZoneData: {
      get() {
        return {
          useDaylightTime: this.customTimeZoneHasDaylightSavings,
          name: this.customTimeZoneName,
          value: this.customTimeZoneOffset,
        };
      },
      set(v) {
        this.customTimeZoneHasDaylightSavings = v.useDaylightTime;
        this.customTimeZoneName = v.name;
        this.customTimeZoneOffset = v.value;
      }
    },
    customTimeZone: {
      get() {
        return this.getPropertyValue('customTimeZone');
      },
      set(v) {
        this.updateOneElementValue('customTimeZone', v);
      },
    },
    customTimeZoneHasDaylightSavings: {
      get() {
        return this.getPropertyValue('customTimeZoneHasDaylightSavings');
      },
      set(v) {
        this.updateOneElementValue('customTimeZoneHasDaylightSavings', v);
      },
    },
    customTimeZoneName: {
      get() {
        return this.getPropertyValue('customTimeZoneName');
      },
      set(v) {
        this.updateOneElementValue('customTimeZoneName', v);
      },
    },
    customTimeZoneOffset: {
      get() {
        return this.getPropertyValue('customTimeZoneOffset');
      },
      set(v) {
        this.updateOneElementValue('customTimeZoneOffset', v);
      },
    },
    fontSize: {
      get() {
        return this.getPropertyValue('styles.fontSize');
      },
      set(v) {
        this.updateOneElementValue('styles.fontSize', v);
      },
    },
    letterSpacing: {
      get() {
        return this.getPropertyValue('styles.letterSpacing');
      },
      set(v) {
        this.updateOneElementValue('styles.letterSpacing', v);
      },
    },
    color: {
      get() {
        return this.getPropertyValue('styles.color');
      },
      set(v) {
        this.updateOneElementValue('styles.color', v);
      },
    },
    fitText: {
      get() {
        return this.getPropertyValue('fitText');
      },
      set(v) {
        this.updateOneElementValue('fitText', v);
      },
    },
    textShadow: {
      get() {
        return this.getPropertyValue('textShadow');
      },
      set(v) {
        this.updateOneElementValue('textShadow', v);
      },
    },
    textShadowX: {
      get() {
        return this.getPropertyValue('textShadowX');
      },
      set(v) {
        this.updateOneElementValue('textShadowX', v);
      },
    },
    textShadowY: {
      get() {
        return this.getPropertyValue('textShadowY');
      },
      set(v) {
        this.updateOneElementValue('textShadowY', v);
      },
    },
    textShadowBlur: {
      get() {
        return this.getPropertyValue('textShadowBlur');
      },
      set(v) {
        this.updateOneElementValue('textShadowBlur', v);
      },
    },
    textShadowColor: {
      get() {
        return this.getPropertyValue('textShadowColor');
      },
      set(v) {
        this.updateOneElementValue('textShadowColor', v);
      },
    },
    fontWeight() {
      return this.getPropertyValue('styles.fontWeight', { emptyValue: 'normal' });
    },
    fontStyle() {
      return this.getPropertyValue('styles.fontStyle', { emptyValue: 'normal' });
    },
    textDecoration() {
      return this.getPropertyValue('styles.textDecoration');
    },
    textTransform() {
      return this.getPropertyValue('styles.textTransform');
    },
    textAlign() {
      return this.getPropertyValue('styles.textAlign');
    },
    verticalAlign() {
      return this.getPropertyValue('verticalAlign');
    },

    previewDate() {
      return this.element.data?.dataValues?.text ?? new Date();
    },
    formats() {
      return [
        {
          format: 'EEEE MMMM do, yyyy',
          display: dateFormat(
            new Date(this.previewDate),
            'EEEE MMMM do, yyyy'
          ),
        },
        {
          format: 'h:mm',
          display: dateFormat(new Date(this.previewDate), 'h:mm'),
        },
        {
          format: 'h:mm aaaa',
          display: dateFormat(new Date(this.previewDate), 'h:mm aaaa'),
        },
        {
          format: 'h:mm a',
          display: dateFormat(new Date(this.previewDate), 'h:mm a'),
        },
        {
          format: 'h:mm a OOOO',
          display: dateFormat(new Date(this.previewDate), 'h:mm a OOOO'),
        },
        {
          format: 'MMMM d',
          display: dateFormat(new Date(this.previewDate), 'MMMM d'),
        },
        {
          format: 'MMM. d, yyyy',
          display: dateFormat(
            new Date(this.previewDate),
            'MMM. d, yyyy'
          ),
        },
        {
          format: 'M/d/yy',
          display: dateFormat(new Date(this.previewDate), 'M/d/yy'),
        },
        {
          format: 'M/d/yyyy',
          display: dateFormat(new Date(this.previewDate), 'M/d/yyyy'),
        },
        {
          format: 'M/d',
          display: dateFormat(new Date(this.previewDate), 'M/d'),
        },
        {
          format: 'M.d.yyyy',
          display: dateFormat(new Date(this.previewDate), 'M.d.yyyy'),
        },
      ];
    },
    getFontName() {
      var self = this;

      return self.fonts.filter(function (font) {
        return font.font === self.getPropertyValue('styles.fontFamily');
      })[0];
    },
    getFontWeightBtnClass() {
      return this.fontWeight === 'bold'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
    getFontStyleBtnClass() {
      return this.fontStyle === 'italic'
        ? 'btn-style-a'
        : 'btn-style-d';
    },
  },
  methods: {
    formatDate($event) {
      var format = $event.target.value;

      if (!format.length) {
        this.formatError = 'Must have at least one formatting character';
        return;
      }

      try {
        dateFormat(new Date(), format);
      } catch (error) {
        this.formatError = 'Invalid date formatting';
        console.error(error);
        return;
      }

      this.formatError = null;
      this.customFormatStyle = format;
    },
    getDate(format) {
      return dateFormat(new Date(), format);
    },
    textChange() {
      var self = this;
      this.$nextTick(function () {
        self.resizeText();
      });
    },
    setAlignment(style) {
      this.updateOneElementValue('styles.textAlign', style);
    },
    setVerticalAlignment(style) {
      this.updateOneElementValue('verticalAlign', style);
    },
    setFontWeight() {
      if (this.fontWeight === 'normal') {
        this.updateOneElementValue('styles.fontWeight', 'bold');
      } else {
        this.updateOneElementValue('styles.fontWeight', 'normal');
      }
    },
    setFontStyle() {
      if (this.fontStyle === 'normal') {
        this.updateOneElementValue('styles.fontStyle', 'italic');
      } else {
        this.updateOneElementValue('styles.fontStyle', 'normal');
      }
    },
    setDecorationStyle(style) {
      this.updateOneElementValue('styles.textDecoration', style);
    },
    setTransformStyle(style) {
      this.updateOneElementValue('styles.textTransform', style);
    },
    setFont(font) {
      this.updateOneElementValue('styles.fontFamily', font);
    },
    resizeText() {
      EventBus.emit('resize-text');
    },
  },
};
</script>
